import { Bold, Italic, Link2, List, ListOrdered, Quote, RemoveFormatting, Underline } from "lucide-react";
import { useEffect, useRef, type ClipboardEvent, type MouseEvent } from "react";

type Props = {
  value: string;
  onChange: (html: string) => void;
  placeholder: string;
  disabled?: boolean;
  minHeight?: number;
  label?: string;
};

const ALLOWED = new Set([
  "P", "DIV", "BR", "STRONG", "B", "EM", "I", "U", "S", "STRIKE",
  "UL", "OL", "LI", "BLOCKQUOTE", "A", "PRE", "CODE", "H3", "H4", "SPAN",
]);
const BLOCKED = new Set([
  "SCRIPT", "STYLE", "SVG", "MATH", "IFRAME", "OBJECT", "EMBED", "FORM",
  "INPUT", "BUTTON", "IMG", "VIDEO", "AUDIO", "META", "LINK", "TEMPLATE",
]);

/** Keep formatting on paste, but not active content, images or unsafe URLs. */
function safeUrl(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const normalized = trimmed.startsWith("www.") ? "https://" + trimmed : trimmed;
  try {
    const url = new URL(normalized, window.location.origin);
    if (!["https:", "http:", "mailto:"].includes(url.protocol)) return null;
    return normalized;
  } catch {
    return null;
  }
}

function sanitizePastedHtml(html: string): string {
  const source = new DOMParser().parseFromString(html, "text/html");
  const output = document.createElement("div");

  const cleanNode = (node: Node): Node | null => {
    if (node.nodeType === Node.TEXT_NODE) {
      return document.createTextNode(node.textContent ?? "");
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return null;

    const element = node as HTMLElement;
    const tag = element.tagName.toUpperCase();
    if (BLOCKED.has(tag)) return null;

    const children = document.createDocumentFragment();
    Array.from(element.childNodes).forEach((child) => {
      const cleaned = cleanNode(child);
      if (cleaned) children.appendChild(cleaned);
    });

    if (!ALLOWED.has(tag)) return children;

    const safeTag = tag === "SPAN" ? "span" : tag.toLowerCase();
    const result = document.createElement(safeTag);

    if (tag === "A") {
      const href = safeUrl(element.getAttribute("href") ?? "");
      if (href) {
        result.setAttribute("href", href);
        result.setAttribute("rel", "noopener noreferrer");
      }
    }

    result.appendChild(children);

    // Pasted Office/webmail text commonly carries emphasis on spans.
    if (tag === "SPAN") {
      const style = element.style;
      const weight = style.fontWeight.toLowerCase();
      const strong = weight === "bold" || Number(weight) >= 600;
      const italic = style.fontStyle.toLowerCase() === "italic";
      const underline = style.textDecorationLine.includes("underline");
      let content: Node = result;
      if (strong) {
        const wrapper = document.createElement("strong");
        wrapper.appendChild(content);
        content = wrapper;
      }
      if (italic) {
        const wrapper = document.createElement("em");
        wrapper.appendChild(content);
        content = wrapper;
      }
      if (underline) {
        const wrapper = document.createElement("u");
        wrapper.appendChild(content);
        content = wrapper;
      }
      return content;
    }

    return result;
  };

  Array.from(source.body.childNodes).forEach((node) => {
    const clean = cleanNode(node);
    if (clean) output.appendChild(clean);
  });
  return output.innerHTML;
}

/** Non-visual validation; HTML tags alone must not count as a message. */
export function hasRichTextContent(html: string): boolean {
  const text = html
    .replace(/<[^>]*>/g, "")
    .replace(/&(?:nbsp|#160|#xA0);/gi, " ")
    .replace(/\s/g, "");
  return text.length > 0;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  disabled = false,
  minHeight = 150,
  label = "Mensagem",
}: Props) {
  const surface = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Keep the selection/caret under browser control while the user types.
    // Only clear the editable DOM after a successfully published message.
    if (value === "" && surface.current && surface.current.innerHTML !== "") {
      surface.current.innerHTML = "";
    }
  }, [value]);

  const emit = () => onChange(surface.current?.innerHTML ?? "");

  const format = (command: string, argument?: string) => {
    if (disabled) return;
    surface.current?.focus();
    document.execCommand(command, false, argument);
    emit();
  };

  const onPaste = (event: ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    const rich = event.clipboardData.getData("text/html");
    const plain = event.clipboardData.getData("text/plain");
    if (rich) {
      document.execCommand("insertHTML", false, sanitizePastedHtml(rich));
    } else {
      document.execCommand("insertText", false, plain);
    }
    emit();
  };

  const protectSelection = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const addLink = () => {
    if (disabled) return;
    const selected = window.getSelection()?.toString() ?? "";
    if (!selected.trim()) {
      surface.current?.focus();
      return;
    }
    const value = window.prompt("Endereço do link (https://...)", "https://");
    if (value === null) return;
    const href = safeUrl(value);
    if (href) format("createLink", href);
  };

  return (
    <div className={"rich-text-editor" + (disabled ? " is-disabled" : "")}>
      <div className="rich-text-toolbar" role="toolbar" aria-label={"Formatação: " + label}>
        <button type="button" title="Negrito (Ctrl+B)" aria-label="Negrito" disabled={disabled} onMouseDown={protectSelection} onClick={() => format("bold")}><Bold size={16} /></button>
        <button type="button" title="Itálico (Ctrl+I)" aria-label="Itálico" disabled={disabled} onMouseDown={protectSelection} onClick={() => format("italic")}><Italic size={16} /></button>
        <button type="button" title="Sublinhado (Ctrl+U)" aria-label="Sublinhado" disabled={disabled} onMouseDown={protectSelection} onClick={() => format("underline")}><Underline size={16} /></button>
        <span className="rich-text-divider" aria-hidden="true" />
        <button type="button" title="Lista com marcadores" aria-label="Lista com marcadores" disabled={disabled} onMouseDown={protectSelection} onClick={() => format("insertUnorderedList")}><List size={16} /></button>
        <button type="button" title="Lista numerada" aria-label="Lista numerada" disabled={disabled} onMouseDown={protectSelection} onClick={() => format("insertOrderedList")}><ListOrdered size={16} /></button>
        <button type="button" title="Citação" aria-label="Citação" disabled={disabled} onMouseDown={protectSelection} onClick={() => format("formatBlock", "blockquote")}><Quote size={16} /></button>
        <span className="rich-text-divider" aria-hidden="true" />
        <button type="button" title="Criar link (selecione o texto)" aria-label="Inserir link" disabled={disabled} onMouseDown={protectSelection} onClick={addLink}><Link2 size={16} /></button>
        <button type="button" title="Remover formatação" aria-label="Remover formatação" disabled={disabled} onMouseDown={protectSelection} onClick={() => format("removeFormat")}><RemoveFormatting size={16} /></button>
      </div>
      <div
        ref={surface}
        className="rich-text-surface"
        contentEditable={!disabled}
        role="textbox"
        aria-label={label}
        aria-multiline="true"
        aria-required="true"
        data-placeholder={placeholder}
        style={{ minHeight }}
        onInput={emit}
        onPaste={onPaste}
        suppressContentEditableWarning
      />
    </div>
  );
}
